/**
 * Product Catalog Types
 *
 * Replaces types/serviceOption.ts. A product is an ORG-LEVEL catalog entry that
 * can be attached to many service-type meeting types, rather than one row bound
 * to a single meeting type.
 *
 * Two shapes matter:
 *   Product              — the catalog entry itself
 *   ProductForMeetingType — that entry AS OFFERED on one meeting type, i.e.
 *                           merged with its attachment (price override, sort
 *                           order, eligible providers)
 *
 * Prefer `effectivePrice` over `price` anywhere a customer sees a number:
 * `price` is the catalog default, `effectivePrice` is what this meeting type
 * actually charges.
 */

import { PointsExpiryUnit } from '../utils/pointsExpiry';

export type ProductKind = 'service_item' | 'subscription' | 'point_pack';
/** Subscriptions bill monthly. Annual billing was removed deliberately. */
export type ProductBillingInterval = 'month';

/**
 * What a product's `price` is in. 'points' makes points behave like a currency
 * — the same price field, a different unit. Single products only.
 */
export type ProductPriceUnit = 'currency' | 'points';
export type ProductAllowancePeriod = 'day' | 'week' | 'month';

/**
 * Icon type
 * - 'preset':   icon from the preset library (iconPreset holds the key)
 * - 'uploaded': custom uploaded image (iconUrl holds the URL)
 * - null:       no icon
 */
export type ProductIconType = 'preset' | 'uploaded' | null;

/** Provider (host) able to deliver a product on a given meeting type. */
export interface ServiceProvider {
  userId: number;
  displayName: string;
  bio?: string;
  avatarUrl?: string;
  additionalFee: number;
  email?: string;
}

/** One image in a product's gallery. Stored now, rendered later. */
export interface ProductImage {
  url: string;
  sortOrder: number;
  alt?: string | null;
}

/** A catalog entry. */
export interface Product {
  id: number;
  organizationId?: number | null;
  userId: number;
  kind: ProductKind;
  title: string;
  description?: string | null;
  durationMinutes?: number | null;
  price?: number | null;
  currency: string;
  priceUnit?: ProductPriceUnit;
  billingInterval?: ProductBillingInterval | null;
  allowanceCount?: number | null;
  allowancePeriod?: ProductAllowancePeriod | null;
  pointsGranted?: number | null;
  /**
   * point_pack only — how long the granted points last. A PAIR, and both null
   * means they never expire. Resolved to a date at grant time and stamped on
   * the ledger row, so editing these decides what the NEXT buyer gets and never
   * touches points already sold.
   */
  pointsExpiryValue?: number | null;
  pointsExpiryUnit?: PointsExpiryUnit | null;
  heroImageUrl?: string | null;
  images?: ProductImage[];
  iconUrl?: string | null;
  iconType?: ProductIconType;
  iconPreset?: string | null;
  isActive: boolean;
  /**
   * Tier family. A plan with a parent is one rung of that parent's ladder, so
   * an upgrade or downgrade has an obvious set of destinations.
   */
  /** Present on GET /api/products/:id — the tickbox selection. */
  entitlementMeetingTypeIds?: number[];
  createdAt?: string;
  updatedAt?: string;
}

/** A catalog entry as offered on one meeting type (catalog + attachment). */
export interface ProductForMeetingType extends Product {
  /** `priceOverride ?? price` — what the invitee actually pays here. */
  effectivePrice?: number | null;
  sortOrder: number;
  providerUserIds?: number[] | null;
  /**
   * Whether THIS meeting type offers the product. Distinct from `isActive`,
   * which is the catalog entry's own state. The meeting-type editor toggles
   * this; the Products page toggles `isActive`.
   */
  attachmentIsActive?: boolean;
  /** Points this service costs here. Null = fall back to the meeting type's. */
  pointCost?: number | null;
  providers?: ServiceProvider[];
}

/** Payload for creating a catalog product. */
export interface CreateProductData {
  kind?: ProductKind;
  title: string;
  description?: string | null;
  durationMinutes?: number | null;
  price?: number | null;
  currency?: string;
  /** 'points' prices a single product in points instead of money. */
  priceUnit?: ProductPriceUnit;
  billingInterval?: ProductBillingInterval | null;
  allowanceCount?: number | null;
  allowancePeriod?: ProductAllowancePeriod | null;
  pointsGranted?: number | null;
  pointsExpiryValue?: number | null;
  pointsExpiryUnit?: PointsExpiryUnit | null;
  iconType?: ProductIconType;
  iconPreset?: string | null;
  isActive?: boolean;
  /** Null takes the product out of whatever family it was in. */
  /**
   * The ordered gallery. Position carries the meaning: index 0 is the main
   * image, which is what `heroImageUrl` mirrors for consumers that only want
   * one (listings, the booking page).
   */
  images?: ProductImage[];
  heroImageUrl?: string | null;
  /** Attach on create — the "add a service to this meeting type" path. */
  meetingTypeIds?: number[];
  /**
   * Attachment data, not catalog data. Accepted here because the service-item
   * form collects it alongside the product fields; `createAndAttach` splits it
   * back out and writes it to the attachment.
   */
  providerUserIds?: number[] | null;
  /** Meeting types this subscription / credit pack unlocks. */
  entitlementMeetingTypeIds?: number[];
}

export type UpdateProductData = Partial<CreateProductData>;

/** Per-attachment settings: how one meeting type offers a product. */
export interface AttachmentData {
  sortOrder?: number;
  priceOverride?: number | null;
  providerUserIds?: number[] | null;
  isActive?: boolean;
}

/** Selection state while booking a service. */
export interface ServiceBookingSelection {
  productId: number;
  selectedProviderId: number | null; // null = "No Preference"
  serviceName: string;
  providerName: string | null;
  durationMinutes: number;
  servicePrice: number;
  providerFee: number;
  totalPrice: number;
}

/** What the public booking page receives for each offered product. */
export interface PublicProduct {
  id: number;
  title: string;
  description?: string | null;
  durationMinutes?: number | null;
  price?: number | null;
  effectivePrice?: number | null;
  currency: string;
  heroImageUrl?: string | null;
  iconUrl?: string | null;
  iconType?: ProductIconType;
  iconPreset?: string | null;
  /** Points this service costs, when the meeting type charges points. */
  pointCost?: number | null;
  /** What `price` is in. A single product may be sold for points. */
  priceUnit?: ProductPriceUnit;
  /** Which sort of product this option is — a plan reads differently. */
  kind?: ProductKind;
  /** Subscriptions: bookings included, and over what period. NULL = unlimited. */
  allowanceCount?: number | null;
  allowancePeriod?: ProductAllowancePeriod | null;
  providers: ServiceProvider[];
}

export interface PublicProvider {
  userId: number;
  displayName: string;
  bio?: string;
  avatarUrl?: string;
  additionalFee: number;
}

/** The price to show for a product on a booking page. */
export const resolveProductPrice = (
  product: Pick<PublicProduct, 'price' | 'effectivePrice'>
): number => Number(product.effectivePrice ?? product.price ?? 0);

/**
 * Whether this product's price is a number of POINTS rather than money.
 *
 * The product's own `priceUnit` is the answer. `pointsMode` on a page is only a
 * fallback for a meeting type carrying its own `pointCost` from before products
 * had one — a page that accepts points OR a plan must not turn every option on
 * it into a points option.
 */
export const isPointsPriced = (
  product: Pick<PublicProduct, 'priceUnit'>,
  pageChargesPoints = false
): boolean => (product.priceUnit != null ? product.priceUnit === 'points' : pageChargesPoints);

/**
 * How many points this product costs — THE SAME CHAIN THE SERVER CHARGES ON.
 *
 * `EntitlementService.resolvePointCost` reads: the attachment's own `pointCost`
 * first (the same product can cost differently on two pages), then the
 * product's price when it is denominated in points, then the meeting type's.
 * Every screen that shows a point cost has to walk that chain in that order —
 * the booking page used to skip the attachment override for a points-priced
 * product, so it advertised one number and the server deducted another.
 */
export const resolveProductPointCost = (
  product: Pick<PublicProduct, 'price' | 'priceUnit' | 'pointCost'>,
  meetingTypePointCost?: number | null
): number => {
  if (product.pointCost != null) return Number(product.pointCost);
  if (product.priceUnit === 'points' && product.price != null) return Number(product.price);
  return Number(meetingTypePointCost ?? 0);
};

/**
 * A meeting type as the product surfaces list it — used for the booking-path
 * hints beside each page name.
 */
export interface PickableMeetingType {
  id: number;
  title: string;
  slug?: string | null;
  type?: string;
}
